import { useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { DuplicateGroup, PromotionCandidate, RatingConflict, SkippedRow } from "@trackmark/core";
import styles from "./Import.module.css";

/**
 * First-run import.
 *
 * Four steps, and the order is the point: choose a file, read what will happen, answer the two
 * questions the data actually raises, then apply. Nothing is written until the last one.
 *
 * The summary speaks in the add-preview's voice — it states counts and consequences before it
 * acts, never after. See docs/IMPORT-PROPOSAL.md §5.1.
 */

export interface ImportDryRun {
  runId: number;
  songs: number;
  albums: number;
  artists: number;
  skippedCount: number;
  duplicateGroups: number;
  duplicateRows: number;
  newRows: number;
  matched: number;
  conflictCount: number;
  ratingsOffered: number;
  skipped: SkippedRow[];
  duplicates: DuplicateGroup[];
  conflicts: RatingConflict[];
  candidates: PromotionCandidate[];
}

export interface ImportResult {
  songsCreated: number;
  albumsCreated: number;
  artistsCreated: number;
  ratingsAdded: number;
  ratingsOverwritten: number;
  ratingsKept: number;
  collapsed: number;
  albumsPromoted: number;
}

export interface ImportProps {
  onDryRun: (filename: string, text: string) => Promise<ImportDryRun>;
  onApply: (
    runId: number,
    options: { collapseDuplicates: boolean; overwriteLines: number[]; promote: string[] },
  ) => Promise<ImportResult>;
  onCancel?: (runId: number) => Promise<void>;
  onDone?: () => void;
}

type Step = "choose" | "review" | "applying" | "done";

/** The proposal's suggested starting point. Arbitrary, which is exactly why it is adjustable. */
const DEFAULT_THRESHOLD = 5;
const THRESHOLDS = [2, 3, 5, 8, 12];

const n = (value: number) => value.toLocaleString();

export function Import({ onDryRun, onApply, onCancel, onDone }: ImportProps) {
  const [step, setStep] = useState<Step>("choose");
  const [diff, setDiff] = useState<ImportDryRun | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [collapse, setCollapse] = useState(true);
  const [overwrite, setOverwrite] = useState<Set<number>>(new Set());
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [declined, setDeclined] = useState<Set<string>>(new Set());
  const [showSkipped, setShowSkipped] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * The whole candidate list arrives once; moving the threshold filters it here rather than
   * asking the server again. Seeing what 3+ and 8+ look like before committing is the point
   * (docs/DESIGN-CONFLICTS.md §J5) and it should be instant.
   */
  const candidates = useMemo(
    () => (diff?.candidates ?? []).filter((c) => c.trackCount >= threshold && !c.looksLikeSingle),
    [diff, threshold],
  );

  const promote = useMemo(() => candidates.filter((c) => !declined.has(c.key)).map((c) => c.key), [candidates, declined]);

  const choose = async (file: File) => {
    setError(null);
    try {
      setDiff(await onDryRun(file.name, await file.text()));
      setStep("review");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the file could not be read");
    }
  };

  const apply = async () => {
    if (!diff) return;
    setStep("applying");
    try {
      setResult(await onApply(diff.runId, { collapseDuplicates: collapse, overwriteLines: [...overwrite], promote }));
      setStep("done");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the import failed");
      setStep("review");
    }
  };

  const cancel = async () => {
    if (diff && onCancel) await onCancel(diff.runId).catch(() => {});
    setDiff(null);
    setStep("choose");
  };

  return (
    <div className={styles.page} data-testid="import">
      <div className={styles.title}>Import</div>

      {step === "choose" && (
        <div className={styles.intro}>
          <p className={styles.lede}>
            An import seeds the <b>library</b> — the songs to then rate. It cannot bring ratings that do not exist
            anywhere: nobody holds a 1–10 judgement of your collection but you.
          </p>
          <p className={styles.note}>
            A Spotify export from Exportify works as-is. Any CSV with artist, album and track columns works too; a
            rating column is read if present.
          </p>

          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className={styles.file}
            data-testid="import-file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void choose(file);
            }}
          />
          {/* A rejected file lands back here with no diff, so the error has to render on this
              step too — otherwise the only feedback is a file picker that appears to do nothing. */}
          {error && (
            <div className={styles.error} data-testid="import-error">
              {error}
            </div>
          )}

          <button type="button" className={styles.primary} onClick={() => fileInput.current?.click()}>
            Choose a CSV
          </button>

          {/* The other half of an import. What you own and what you play are different files and
              different questions, so they are different screens rather than one with a mode. */}
          <p className={styles.note} style={{ marginTop: 20, marginBottom: 0 }}>
            Importing <b>listening history</b> instead — what you actually played, from a Spotify data download?{" "}
            <Link to="/import/history" className={styles.link} data-testid="go-listening-import">
              That is over here
            </Link>
            .
          </p>
        </div>
      )}

      {step !== "choose" && diff && !result && (
        <div className={styles.review}>
          {/* The headline, stated before anything happens. */}
          <div className={styles.summary} data-testid="import-summary">
            <div className={styles.counts}>
              <span data-testid="count-songs">{n(diff.songs)} SONGS</span>
              <span className={styles.dot}>·</span>
              <span>{n(diff.albums)} ALBUMS</span>
              <span className={styles.dot}>·</span>
              <span>{n(diff.artists)} ARTISTS</span>
            </div>

            {diff.matched > 0 && (
              <div className={styles.line} data-testid="matched-line">
                {n(diff.newRows)} new · {n(diff.matched)} already in your library
              </div>
            )}

            {diff.skippedCount > 0 && (
              <div className={styles.line}>
                <span className={styles.warn} data-testid="skipped-line">
                  {n(diff.skippedCount)} {diff.skippedCount === 1 ? "row" : "rows"} skipped
                </span>
                <button type="button" className={styles.link} onClick={() => setShowSkipped((v) => !v)}>
                  {showSkipped ? "Hide" : "Review"}
                </button>
              </div>
            )}
          </div>

          {showSkipped && (
            <div className={styles.detail} data-testid="skipped-detail">
              {diff.skipped.map((row) => (
                <div key={row.line} className={styles.detailRow}>
                  <span className={styles.lineNo}>LINE {row.line}</span>
                  <span>{row.reason}</span>
                </div>
              ))}
            </div>
          )}

          {/* Question one: 17% of the measured file is duplicate rows. That is a flood, not a
              trickle, so it is answered here rather than one album at a time later. */}
          {diff.duplicateGroups > 0 && (
            <section className={styles.question} data-testid="duplicates-question">
              <h2 className={styles.heading}>Duplicates</h2>
              <p className={styles.body}>
                {n(diff.duplicateRows)} rows look like {n(diff.duplicateGroups)}{" "}
                {diff.duplicateGroups === 1 ? "song" : "songs"} — a track and its remaster, mostly.
              </p>
              <div className={styles.choices}>
                <button
                  type="button"
                  className={styles.choice}
                  data-active={collapse}
                  data-testid="collapse-yes"
                  onClick={() => setCollapse(true)}
                >
                  Import as {n(diff.duplicateGroups)} songs
                </button>
                <button
                  type="button"
                  className={styles.choice}
                  data-active={!collapse}
                  data-testid="collapse-no"
                  onClick={() => setCollapse(false)}
                >
                  Keep all {n(diff.duplicateRows)} separate
                </button>
                <button type="button" className={styles.link} onClick={() => setShowDuplicates((v) => !v)}>
                  {showDuplicates ? "Hide" : "Review"}
                </button>
              </div>

              {showDuplicates && (
                <div className={styles.detail} data-testid="duplicates-detail">
                  {diff.duplicates.slice(0, 40).map((group) => (
                    <div key={`${group.artist}-${group.signature}`} className={styles.detailRow}>
                      <span className={styles.lineNo}>{group.artist}</span>
                      <span>{group.titles.join("  ·  ")}</span>
                    </div>
                  ))}
                  {diff.duplicates.length > 40 && (
                    <div className={styles.detailRow}>
                      <span className={styles.lineNo} />
                      <span>and {n(diff.duplicates.length - 40)} more</span>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Question two: ratings. The only irreversible thing an import can do. */}
          {diff.conflictCount > 0 && (
            <section className={styles.question} data-testid="conflicts-question">
              <h2 className={styles.heading}>Ratings that disagree</h2>
              <p className={styles.body}>
                {n(diff.conflictCount)} {diff.conflictCount === 1 ? "song has" : "songs have"} a different rating in
                this file. <b>Your ratings are kept</b> unless you say otherwise — the file is a seed, not an
                authority.
              </p>
              <div className={styles.conflicts}>
                {diff.conflicts.map((c) => (
                  <label key={c.line} className={styles.conflict}>
                    <input
                      type="checkbox"
                      checked={overwrite.has(c.line)}
                      data-testid={`overwrite-${c.line}`}
                      onChange={(e) =>
                        setOverwrite((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(c.line);
                          else next.delete(c.line);
                          return next;
                        })
                      }
                    />
                    <span className={styles.conflictTitle}>{c.title}</span>
                    <span className={styles.conflictArtist}>{c.artist}</span>
                    <span className={styles.keepValue}>{c.current}</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.incomingValue}>{c.incoming}</span>
                  </label>
                ))}
              </div>
              <div className={styles.note}>
                {overwrite.size === 0
                  ? "No rating will change."
                  : `${n(overwrite.size)} ${overwrite.size === 1 ? "rating" : "ratings"} will be replaced.`}
              </div>
            </section>
          )}

          {/* Album promotion — once, here, with the threshold adjustable in the moment (§J5). */}
          {diff.candidates.length > 0 && (
            <section className={styles.question} data-testid="promotion-question">
              <h2 className={styles.heading}>Records you probably own</h2>
              <p className={styles.body}>
                Most albums here are one liked track, not a record. Marking one as owned records that — it does not
                add tracks you did not import, and it does not produce an album score until the full track list is
                pulled.
              </p>

              <div className={styles.thresholds}>
                <span>AT LEAST</span>
                {THRESHOLDS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={styles.filter}
                    data-active={threshold === t}
                    data-testid={`threshold-${t}`}
                    onClick={() => setThreshold(t)}
                  >
                    {t}
                  </button>
                ))}
                <span>TRACKS</span>
                <span className={styles.count} data-testid="candidate-count">
                  {n(promote.length)} OF {n(candidates.length)} SELECTED
                </span>
              </div>

              <div className={styles.candidates} data-testid="candidates">
                {candidates.map((c) => (
                  <label key={c.key} className={styles.candidate} data-testid="candidate">
                    <input
                      type="checkbox"
                      checked={!declined.has(c.key)}
                      onChange={(e) =>
                        setDeclined((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.delete(c.key);
                          else next.add(c.key);
                          return next;
                        })
                      }
                    />
                    <span className={styles.candidateAlbum}>{c.album}</span>
                    <span className={styles.candidateArtist}>{c.artist}</span>
                    <span className={styles.candidateCount}>{c.trackCount} TRACKS</span>
                  </label>
                ))}
                {candidates.length === 0 && <div className={styles.empty}>Nothing meets that threshold.</div>}
              </div>
            </section>
          )}

          {error && <div className={styles.error} data-testid="import-error">{error}</div>}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              data-testid="import-apply"
              disabled={step === "applying"}
              onClick={() => void apply()}
            >
              {step === "applying" ? "Importing…" : `Import ${n(collapse ? diff.songs - (diff.duplicateRows - diff.duplicateGroups) : diff.songs)} songs`}
            </button>
            <button type="button" className={styles.secondary} data-testid="import-cancel" onClick={() => void cancel()}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div className={styles.done} data-testid="import-done">
          <div className={styles.counts}>
            <span data-testid="created-songs">{n(result.songsCreated)} SONGS</span>
            <span className={styles.dot}>·</span>
            <span>{n(result.albumsCreated)} ALBUMS</span>
            <span className={styles.dot}>·</span>
            <span>{n(result.artistsCreated)} ARTISTS</span>
          </div>

          <ul className={styles.receipt}>
            {result.collapsed > 0 && <li>{n(result.collapsed)} duplicate rows collapsed</li>}
            {result.albumsPromoted > 0 && (
              <li>{n(result.albumsPromoted)} albums marked as owned — track lists pull separately</li>
            )}
            {result.ratingsAdded > 0 && <li>{n(result.ratingsAdded)} ratings added</li>}
            {result.ratingsOverwritten > 0 && <li>{n(result.ratingsOverwritten)} ratings replaced</li>}
            {result.ratingsKept > 0 && (
              <li data-testid="kept-line">{n(result.ratingsKept)} of your ratings kept over the file&rsquo;s</li>
            )}
          </ul>

          <p className={styles.lede}>
            Artwork is filling in behind you. The library is rateable now.
          </p>
          <button type="button" className={styles.primary} data-testid="import-start-rating" onClick={onDone}>
            Start rating
          </button>
        </div>
      )}
    </div>
  );
}
